import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Input,
  Button,
  Table,
  Space,
  Popconfirm,
  message,
  Tag,
  Select,
  Grid
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import ModalAdd from './ModalAdd';
import {
  apiGetMasterData,
  apiCreateMasterData,
  apiUpdateMasterData,
  apiDeleteMasterData
} from '@services/MasterData/MasterDataService';
import { usePermission } from '@contexts/Auth/UsePermission';

const { Option } = Select;
const { useBreakpoint } = Grid;

const List = ({ activeType: propActiveType = 'CN' }) => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ page: 1, size: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalInitialValues, setModalInitialValues] = useState(null);

  const [parent1Filter, setParent1Filter] = useState(undefined);
  const [parent2Filter, setParent2Filter] = useState(undefined);
  const [parent1Options, setParent1Options] = useState([]);
  const [parent2Options, setParent2Options] = useState([]);
  const screens = useBreakpoint();
  const { masterData } = usePermission();

  const [activeType, setActiveType] = useState(propActiveType);

  // sync prop changes and clear old state when tab changes
  useEffect(() => {
    if (propActiveType !== activeType) {
      // keep general UI state reset
      setSearch('');
      setList([]);
      setMeta({ page: 1, size: 10, total: 0 });
      setEditing(null);
      setModalVisible(false);
      setLoading(false);
      setModalInitialValues(null);

      // preserve or clear parent filters depending on target tab:
      // - KV expects parent1 (Chi nhánh) — keep parent1Filter (don't reset)
      // - DHK expects both parents — keep existing values
      // - CN / TUYEN do not use parents — clear both
      if (propActiveType === 'KV' || propActiveType === 'DHK') {
        // keep parent1Filter (and parent2Filter for DHK)
        if (propActiveType === 'KV') {
          setParent2Filter(undefined); // ensure lower parent cleared
        }
        // parent options will be (re)fetched by fetchParents effect when activeType changes
      } else {
        // CN or TUYEN -> clear parents and options
        setParent1Filter(undefined);
        setParent2Filter(undefined);
        setParent1Options([]);
        setParent2Options([]);
      }

      setActiveType(propActiveType);
    } else {
      setActiveType(propActiveType);
    }
  }, [propActiveType, activeType]);

  // fetch parent options based on activeType
  useEffect(() => {
    const fetchParents = async () => {
      try {
        if (activeType === 'KV') {
          // parents are Chi nhánh (CN)
          const res = await apiGetMasterData({ type: 'CN', page: 1, size: 1000 });
          const data = res.data?.data ?? res.data ?? [];
          setParent1Options(Array.isArray(data) ? data : []);
          setParent2Options([]);
        } else if (activeType === 'DHK') {
          // parent_level_2 = CN (always fetch all CN)
          const res2 = await apiGetMasterData({ type: 'CN', page: 1, size: 1000 });
          const d2 = res2.data?.data ?? res2.data ?? [];
          setParent2Options(Array.isArray(d2) ? d2 : []);
          // parent_level_1 = KV (will be fetched based on selected CN below)
        } else {
          // CN / TUYEN tab -> no parents
          setParent1Options([]);
          setParent2Options([]);
        }
      } catch (e) {
        // ignore parent-fetch errors
      }
    };
    fetchParents();
  }, [activeType]);

  // cascading: when parent2Filter (CN) changes in DHK tab, fetch KV under that CN
  useEffect(() => {
    if (activeType === 'DHK' && parent2Filter) {
      const fetchKVUnderCN = async () => {
        try {
          const res = await apiGetMasterData({
            type: 'KV',
            parent_level_1_id: parent2Filter,
            page: 1,
            size: 1000
          });
          const data = res.data?.data ?? res.data ?? [];
          setParent1Options(Array.isArray(data) ? data : []);
        } catch (e) {
          // ignore
        }
      };
      fetchKVUnderCN();
    } else if (activeType === 'DHK' && !parent2Filter) {
      // if no CN selected, show all KV
      const fetchAllKV = async () => {
        try {
          const res = await apiGetMasterData({ type: 'KV', page: 1, size: 1000 });
          const data = res.data?.data ?? res.data ?? [];
          setParent1Options(Array.isArray(data) ? data : []);
        } catch (e) {
          // ignore
        }
      };
      fetchAllKV();
    }
  }, [activeType, parent2Filter]);

  const fetch = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const p = {
          page: params.page ?? meta.page,
          size: params.size ?? meta.size,
          keyword: params.keyword ?? search ?? undefined,
          type: params.type ?? activeType ?? undefined,
          parent_level_1_id: params.parent_level_1_id ?? parent1Filter ?? undefined,
          parent_level_2_id: params.parent_level_2_id ?? parent2Filter ?? undefined
        };
        const res = await apiGetMasterData(p);
        const data = res.data?.data ?? res.data ?? [];
        const pagination = res.data?.meta ?? { total: res.data?.length ?? data.length };
        setList(Array.isArray(data) ? data : []);
        setMeta((m) => ({ ...m, total: pagination.total ?? 0, page: p.page, size: p.size }));
      } catch (e) {
        message.error('Lấy dữ liệu thất bại');
      } finally {
        setLoading(false);
      }
    },
    [search, meta.page, meta.size, activeType, parent1Filter, parent2Filter]
  );

  useEffect(() => {
    fetch({ page: 1, type: activeType });
  }, [fetch, activeType]);

  const handleSearch = () => {
    fetch({ page: 1, keyword: search });
  };

  const handleTableChange = (pagination) => {
    fetch({ page: pagination.current, size: pagination.pageSize });
  };

  const openCreate = () => {
    setEditing(null);
    // prefill modal initial values so parent relationships are clear
    const defaults = { type: activeType };
    if (activeType === 'KV') {
      // chi nhánh is parent of KV -> parent_level_1 is CN
      defaults.parent_level_1_id = parent1Filter ?? undefined;
    } else if (activeType === 'DHK') {
      // KV is parent_level_1, CN is parent_level_2
      defaults.parent_level_1_id = parent1Filter ?? undefined;
      defaults.parent_level_2_id = parent2Filter ?? undefined;
    }
    setModalInitialValues(defaults);
    setModalVisible(true);
  };

  const openEdit = (record) => {
    // setEditing(record);
    // // ensure modal receives existing parent ids (use id fields)
    // setModalInitialValues({
    //   ...record,
    //   parent_level_1_id: record.parent_level_1_id ?? record.parent_level_1_id,
    //   parent_level_2_id: record.parent_level_2_id ?? record.parent_level_2_id
    // });
    // setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (editing && editing.id) {
        await apiUpdateMasterData(editing.id, values);
        message.success('Cập nhật thành công');
      } else {
        await apiCreateMasterData(values);
        message.success('Tạo mới thành công');
      }
      setModalVisible(false);
      setModalInitialValues(null);
      fetch({ page: 1 });
    } catch (e) {
      message.error('Thao tác thất bại');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiDeleteMasterData(id);
      message.success('Xoá thành công');
      fetch({ page: 1 });
    } catch (e) {
      message.error('Xoá thất bại');
    }
  };

  const columns = useMemo(() => {
    const cols = [
      { title: 'Mã', dataIndex: 'value', width: 120 },
      { title: 'Tên hiển thị', dataIndex: 'name' }
    ];

    // Nếu đang ở tab Chi nhánh (CN) thì ẩn cả Parent 1 và Parent 2
    if (activeType !== 'CN' && activeType !== 'TUYEN') {
      cols.push({
        title: activeType === 'KV' ? 'Thuộc chi nhánh' : 'Thuộc tổ quản lý',
        dataIndex: 'parent_level_1_name'
      });
    }

    // Nếu đang ở tab Đồng hồ khối (DHK) thì hiển thị Parent 2
    if (activeType === 'DHK') {
      cols.push({ title: 'Thuộc chi nhánh', dataIndex: 'parent_level_2_name' });
    }

    cols.push(
      {
        title: 'Trạng thái',
        dataIndex: 'is_active',
        render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Hoạt động' : 'Ngừng'}</Tag>
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        width: 140,
        render: (_, record) => (
          <Space>
            {masterData.edit && <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />}
            {masterData.delete && (
              <Popconfirm title="Xác nhận xoá?" onConfirm={() => handleDelete(record.id)}>
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        )
      }
    );

    return cols;
  }, [activeType]);

  return (
    <div style={{ padding: 16 }}>
      {/* TOP: responsive search and filter section */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }} align="middle">
        {activeType === 'CN' || activeType === 'TUYEN' ? (
          // only show search in first column
          <Col xs={24} sm={24} md={10} lg={10}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Input
                placeholder="Tìm kiếm tên / mã..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
                size={screens.xs ? 'middle' : 'large'}
                style={{ flex: 1, minWidth: 200 }}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                size={screens.xs ? 'middle' : 'large'}>
                Tìm kiếm
              </Button>
            </div>
          </Col>
        ) : (
          // 3-column layout for KV and DHK
          <>
            {/* Column 1: Parent selectors */}
            <Col xs={24} sm={24} md={10} lg={10}>
              <Row gutter={[8, 8]}>
                {activeType === 'DHK' && (
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Select
                      allowClear
                      placeholder="Chọn Chi nhánh"
                      style={{ width: '100%' }}
                      value={parent2Filter}
                      onChange={(val) => {
                        setParent2Filter(val);
                        // clear lower parent when upper changes
                        setParent1Filter(undefined);
                      }}>
                      {parent2Options.map((opt) => (
                        <Option key={opt.id} value={opt.value}>
                          {opt.name}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                )}
                {activeType !== 'CN' && (
                  <Col
                    xs={24}
                    sm={activeType === 'DHK' ? 12 : 24}
                    md={activeType === 'DHK' ? 12 : 24}
                    lg={activeType === 'DHK' ? 12 : 24}>
                    <Select
                      allowClear
                      placeholder={activeType === 'KV' ? 'Chọn Chi nhánh' : 'Chọn Tổ quản lý'}
                      style={{ width: '100%' }}
                      value={parent1Filter}
                      onChange={(val) => {
                        setParent1Filter(val);
                        // for DHK, clear lower if any, but since parent1 is KV, no lower
                      }}>
                      {parent1Options.map((opt) => (
                        <Option key={opt.id} value={opt.value}>
                          {opt.name}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                )}
              </Row>
            </Col>

            {/* Column 2: Search */}
            <Col xs={24} sm={24} md={8} lg={8}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Input
                  placeholder="Tìm kiếm tên / mã..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onPressEnter={handleSearch}
                  allowClear
                  size={screens.xs ? 'middle' : 'large'}
                  style={{ flex: 1, minWidth: 200 }}
                />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  size={screens.xs ? 'middle' : 'large'}>
                  Tìm kiếm
                </Button>
              </div>
            </Col>

            {/* Column 3: Actions */}
            <Col
              xs={24}
              sm={24}
              md={6}
              lg={6}
              style={{
                display: 'flex',
                justifyContent: screens.xs ? 'stretch' : 'flex-end',
                gap: 8,
                flexWrap: 'wrap'
              }}>
              <Button
                type="default"
                onClick={() => {
                  setSearch('');
                  setParent1Filter(undefined);
                  setParent2Filter(undefined);
                  fetch({
                    page: 1,
                    keyword: undefined,
                    parent_level_1_id: undefined,
                    parent_level_2_id: undefined
                  });
                }}
                style={{ minWidth: 96 }}
                block={screens.xs}
                size={screens.xs ? 'middle' : 'large'}>
                Xóa lọc
              </Button>
              {masterData.create && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCreate}
                  style={{ minWidth: 96 }}
                  block={screens.xs}
                  size={screens.xs ? 'middle' : 'large'}>
                  Thêm
                </Button>
              )}
            </Col>
          </>
        )}
      </Row>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        pagination={{ current: meta.page, pageSize: meta.size, total: meta.total }}
        onChange={handleTableChange}
      />

      <ModalAdd
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setModalInitialValues(null);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
        initialValues={modalInitialValues ?? editing}
      />
    </div>
  );
};
export default List;
